import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * The opening hours as a week of rows — customer-portal review #5 (fix round D, 2026-09-25).
 *
 * Each day is closed, or open for one period (or two: a lunch and a dinner service). The times are
 * native `<input type="time">` controls, so a time that is not a time cannot be typed; the week is
 * turned into the sentence the build reads by `./opening-hours.ts`. "Same as Monday for every day"
 * fills the week from the first row, because most businesses keep the same hours. "All day" on an
 * open day sets the whole day, written `00:00-24:00` for the build (review 76, fix round R): a time
 * box cannot hold 24:00, so the whole day is a box of its own.
 */
import { Button } from "@wetalk/ui";
import type { ReactNode } from "react";

import {
  ALL_DAY_PERIOD,
  DAY_NAME,
  MAX_PERIOD_PER_DAY,
  isAllDay,
  type OpeningPeriod,
  type OpeningWeek,
} from "./opening-hours.js";

export interface OpeningHoursEditorProps {
  readonly id: string;
  readonly week: OpeningWeek;
  readonly describedBy: string | undefined;
  readonly invalid: boolean | undefined;
  readonly onWeek: (week: OpeningWeek) => void;
}

/** The period a day opens with when it is marked open: blank, for the person to fill. */
const BLANK_PERIOD: OpeningPeriod = { opensAt: "", closesAt: "" };

export function OpeningHoursEditor({ id, week, describedBy, invalid, onWeek }: OpeningHoursEditorProps): ReactNode {
  usePortalTranslation("operations");
  const setDay = (index: number, period: readonly OpeningPeriod[]): void => {
    onWeek(week.map((one, at) => (at === index ? period : one)));
  };

  return (
    <div className="c-setup-hours" id={id} role="group" aria-describedby={describedBy} aria-invalid={invalid}>
      {DAY_NAME().map((day, index) => {
        const period = week[index] ?? [];
        const open = period.length > 0;
        const allDay = open && period.every(isAllDay);
        return (
          <div key={index} className="c-setup-hours-day">
            <label className="c-setup-hours-open">
              <input
                type="checkbox"
                checked={open}
                onChange={(event) => {
                  const next = event.currentTarget.checked;
                  setDay(index, next ? [BLANK_PERIOD] : []);
                }}
              />
              <span className="c-setup-hours-name">{day}</span>
            </label>
            {open ? (
              <label className="c-setup-hours-open">
                <input
                  type="checkbox"
                  checked={allDay}
                  aria-label={portalText("operations", "value_open_all_day_06010695", { v0: String(day) })}
                  onChange={(event) => {
                    const next = event.currentTarget.checked;
                    setDay(index, next ? [ALL_DAY_PERIOD] : [BLANK_PERIOD]);
                  }}
                />
                <span>{portalText("operations", "all_day_11457433")}</span>
              </label>
            ) : null}
            {allDay ? (
              <span className="c-setup-hours-closed">{portalText("operations", "open_all_day_d60531da")}</span>
            ) : open ? (
              <span className="c-setup-hours-periods">
                {period.map((one, at) => (
                  <span key={at} className="c-setup-hours-period">
                    <input
                      type="time"
                      className="input c-setup-hours-time"
                      aria-label={portalText("operations", "value_opensvalue_85fb35d7", { v0: String(day), v1: String(at > 0 ? portalText("operations", "opening.second_period") : "") })}
                      value={one.opensAt}
                      onChange={(event) => {
                        const opensAt = event.currentTarget.value;
                        setDay(index, period.map((each, which) => (which === at ? { ...each, opensAt } : each)));
                      }}
                    />
                    <span aria-hidden="true">{portalText("operations", "to_4374aaee")}</span>
                    <input
                      type="time"
                      className="input c-setup-hours-time"
                      aria-label={portalText("operations", "value_closesvalue_c1b33bcd", { v0: String(day), v1: String(at > 0 ? portalText("operations", "opening.second_period") : "") })}
                      value={one.closesAt}
                      onChange={(event) => {
                        const closesAt = event.currentTarget.value;
                        setDay(index, period.map((each, which) => (which === at ? { ...each, closesAt } : each)));
                      }}
                    />
                    {at > 0 ? (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setDay(index, period.filter((_each, which) => which !== at));
                        }}
                      >
                        {portalText("operations", "remove_e963907d")}</Button>
                    ) : null}
                  </span>
                ))}
                {period.length < MAX_PERIOD_PER_DAY ? (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDay(index, [...period, BLANK_PERIOD]);
                    }}
                  >
                    {portalText("operations", "add_a_second_period_f6a02872")}</Button>
                ) : null}
              </span>
            ) : (
              <span className="c-setup-hours-closed">{portalText("operations", "closed_88d86b77")}</span>
            )}
          </div>
        );
      })}
      <span className="c-setup-hours-actions">
        <Button
          variant="secondary"
          onClick={() => {
            const first = week[0] ?? [];
            onWeek(DAY_NAME().map(() => first));
          }}
        >
          {portalText("operations", "same_as_monday_for_every_day_46a2bf05")}</Button>
      </span>
    </div>
  );
}
